import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import BenchlingProfiler, {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  getLoadResultsTabExtraMetadata,
  LOAD_RESULTS_TAB_FINISHED_EVENT,
  LOAD_RESULTS_TAB_STARTED_EVENT,
} from 'performance/processResultsEvents';
import {ASSAY_TYPES, AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {ResultViewType} from 'results/assays/AssayResultsConstants';
import {fetchAllAssaySchemas} from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import {DataMissing, getServerResultsTableSpecs} from 'results/reduceResults';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {exportResults} from './AssayRecordActions';
import {AssayResult} from './AssayRecordConstants';
import {fetchFilteredAssayResultsWithPerSchemaLimit, Filters} from './FilteredAssayResultsActions';
import {ResultsTableSpec, ServerResultsTableSpec} from './ResultsTableSpecConstants';
interface ExternalProps {
  filters: Filters;
  includeResultsAgainstArchivedSchemas: boolean;
  resultType?: ResultViewType;
}

interface DispatchProps {
  fetchFilteredAssayResults: (filters: Filters) => void;
}

export interface FilteredAssayResultsConnectedProps {
  resultsTableSpecs: Array<ResultsTableSpec>;
}

/**
 * Adapt Array<ServerResultsTableSpec> to Array<ResultsTableSpec>
 */
function adaptResultsTableSpecsFromServer(
  serverResultsTableSpecs: Array<ServerResultsTableSpec>,
  filters: Filters,
  resultSchemas: Array<AssaySchema>,
  assayResultsById: {[resultId: string]: AssayResult | DataMissing},
  includeResultsAgainstArchivedSchemas: boolean
): Array<ResultsTableSpec> | null {
  const resultSchemasById = _.indexBy(resultSchemas, 'id');
  const resultsTableSpecs = [];
  for (const serverSpec of serverResultsTableSpecs) {
    const schema = resultSchemasById[serverSpec.schemaId];
    if (!schema) {
      continue;
    }
    // Skip results recorded against archived schemas if specified
    if (isArchived(schema) && !includeResultsAgainstArchivedSchemas) {
      continue;
    }
    const assayResults: Array<AssayResult | DataMissing> = [];
    for (const resultId of serverSpec.assayResultIds) {
      const result = assayResultsById[resultId];
      if (!result) {
        return null;
      }
      assayResults.push(result);
    }
    resultsTableSpecs.push({
      schema,
      total: serverSpec.total,
      assayResults,
      isOversized: serverSpec.isOversized,
      isTruncated: serverSpec.isTruncated,
      onDownloadAsCSV: () => {
        exportResults(serverSpec.schemaId, filters);
      },
    });
  }
  return resultsTableSpecs;
}

function getProfilerEventId(filters: Filters): string {
  const filterIds = Object.values(filters)
    .filter((filterId) => filterId != null)
    .join('-');
  return `filters-${filterIds}`;
}

class FilteredAssayResultsConnector<P extends ExternalProps> extends DataConnector<
  P,
  FilteredAssayResultsConnectedProps
> {
  stores = [AssayStore];
  reduxSubscriptionPaths = ['models.results'];

  renderLoading(): JSX.Element {
    return <HorizontalThrobber />;
  }

  _getResultsTableSpecs(): Array<ResultsTableSpec> {
    const serverResultsTableSpecs: Array<ServerResultsTableSpec> | null = getServerResultsTableSpecs(
      this.reduxState().models.results,
      this.props.filters
    );
    if (!serverResultsTableSpecs) {
      return this.fallBackToLoading();
    }
    const resultSchemas: Array<AssaySchema> =
      this.assayStore().getAssaySchemas(ASSAY_TYPES.ASSAY_RESULT) || [];
    const {assayResultsById} = this.reduxState().models.results;
    const resultsTableSpecs = adaptResultsTableSpecsFromServer(
      serverResultsTableSpecs,
      this.props.filters,
      resultSchemas,
      assayResultsById,
      this.props.includeResultsAgainstArchivedSchemas
    );
    if (!resultsTableSpecs) {
      return this.fallBackToLoading();
    }
    return resultsTableSpecs;
  }

  enhanceProps(): FilteredAssayResultsConnectedProps {
    return {
      resultsTableSpecs: this._getResultsTableSpecs(),
    };
  }
}

/**
 * Given filters, load matching AssayResults
 *
 * To handle oversized results, pass to wrapped component grouped by schema as Array<ResultsTableSpec>.
 */
export default function withFilteredAssayResultsLoading<P extends ExternalProps>(
  WrappedComponent: React.ComponentType<P & FilteredAssayResultsConnectedProps>
): React.ComponentClass<P> {
  const ConnectedComponent = FilteredAssayResultsConnector.connect<P, FilteredAssayResultsConnectedProps>(
    (props) => {
      const profilerMetadata = getLoadResultsTabExtraMetadata(
        props.resultsTableSpecs.reduce((total, {assayResults}) => total + assayResults.length, 0),
        props.resultsTableSpecs.length,
        props.resultType
      );
      return (
        <BenchlingProfiler
          {...LOAD_RESULTS_TAB_FINISHED_EVENT}
          id={getProfilerEventId(props.filters)}
          extra={profilerMetadata}
        >
          <WrappedComponent {...props} />
        </BenchlingProfiler>
      );
    }
  );

  /**
   * Fetch the data FilteredAssayResultsConnector needs
   */
  class FetchFilteredAssayResultsWrapper extends React.Component<P & DispatchProps> {
    constructor(props: P & DispatchProps) {
      logProfilerTimingEvent({...LOAD_RESULTS_TAB_STARTED_EVENT, id: getProfilerEventId(props.filters)});
      super(props);
    }

    componentDidMount(): void {
      fetchAllAssaySchemas();
      this.props.fetchFilteredAssayResults(this.props.filters);
    }

    componentDidUpdate(prevProps: P & DispatchProps): void {
      if (!isEqual(this.props.filters, prevProps.filters)) {
        this.props.fetchFilteredAssayResults(this.props.filters);
      }
    }

    render(): JSX.Element {
      return <ConnectedComponent {...this.props} />;
    }
  }

  // @ts-expect-error: propTypes confuses TypeScript; We're going to move away from HOCs anyway.
  return connect<{}, DispatchProps, P>(null, {
    fetchFilteredAssayResults: fetchFilteredAssayResultsWithPerSchemaLimit,
    // @ts-expect-error: propTypes confuses TypeScript; We're going to move away from HOCs anyway.
  })(FetchFilteredAssayResultsWrapper);
}
